import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import Chart from "chart.js/auto";
import { ApiHook } from "../../../hooks/apiHook";

const LegComparisonChart = ({ legs }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !legs || legs.length === 0) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const topLegs = legs.slice(0, 10);

    const ctx = chartRef.current.getContext("2d");
    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: topLegs.map((l) => l.username),
        datasets: [
          {
            label: "Direct Sales",
            data: topLegs.map((l) => l.directSales),
            backgroundColor: "#1cc88a",
          },
          {
            label: "Group Sales",
            data: topLegs.map((l) => l.groupSales),
            backgroundColor: "#4e73df",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top", labels: { font: { size: 11 } } },
        },
        scales: {
          x: { ticks: { font: { size: 10 } } },
          y: { beginAtZero: true, ticks: { font: { size: 10 } } },
        },
      },
    });

    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
    };
  }, [legs]);

  return (
    <div style={{ height: "350px" }}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

const SORT_OPTIONS = [
  { value: "directSales", labelKey: "byDirectSales" },
  { value: "groupSales", labelKey: "byGroupSales" },
  { value: "personalPurchases", labelKey: "byPersonalPurchases" },
  { value: "recentlyJoined", labelKey: "recentlyJoined" },
  { value: "sponsor", labelKey: "bySponsor" },
];

const SubscriptionBadge = ({ active, productValidity, t }) => {
  const dateStr = productValidity ? new Date(productValidity).toLocaleDateString() : "";
  return (
    <span
      className="badge"
      style={{
        backgroundColor: active ? "#1cc88a" : "#e74a3b",
        color: "#fff",
        fontSize: "10px",
      }}
    >
      {active
        ? `${t("subscriptionActive")} ${dateStr}`
        : `${t("subscriptionExpired")} ${dateStr}`}
    </span>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString();
};

const RankBadge = ({ rankName }) => {
  if (!rankName) return <span className="text-muted" style={{ fontSize: "10px" }}>-</span>;
  return (
    <span className="badge bg-primary" style={{ fontSize: "10px" }}>
      {rankName}
    </span>
  );
};

const SponsorCell = ({ username, fullName }) => (
  <div>
    <strong>{username || "-"}</strong>
    {fullName && <div className="text-muted" style={{ fontSize: "10px" }}>{fullName}</div>}
  </div>
);

const LegDrilldownView = ({ legId, startDate, endDate, onBack }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("directSales");
  const pageSize = 20;

  const { data, isLoading, isFetching, isError } = ApiHook.CallLegDetails(
    legId,
    startDate,
    endDate,
    page,
    pageSize,
    sortBy
  );

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" /> {t("loading")}
      </div>
    );
  }

  if (isError || !data?.data) {
    return <div className="alert alert-warning">{t("errorLoadingData")}</div>;
  }

  const { legInfo, summary, topSellers, members, pagination } = data.data;
  const totalPages = Math.ceil(pagination.total / pagination.pageSize) || 1;

  const formatNum = (n) => {
    if (n == null) return "-";
    if (isNaN(n)) return "0";
    return Number(n).toLocaleString();
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <button className="btn btn-sm btn-outline-secondary me-3" onClick={onBack}>
          ← {t("backToLegs")}
        </button>
        <div>
          <h6 className="mb-0">
            {t("legDrilldown")}: {t("legLabel")} {legInfo.legPosition} - {legInfo.username}
            {legInfo.fullName && (
              <small className="text-muted ms-2">({legInfo.fullName})</small>
            )}
            <span className="ms-2">
              <SubscriptionBadge active={legInfo.subscriptionActive} productValidity={legInfo.productValidity} t={t} />
            </span>
            {legInfo.rankName && (
              <span className="ms-2">
                <RankBadge rankName={legInfo.rankName} />
              </span>
            )}
          </h6>
          <small className="text-muted">
            {t("dateOfJoining")}: {formatDate(legInfo.dateOfJoining)}
            {legInfo.performancePercentage > 0 && (
              <span className="ms-3">
                {t("performancePercentage")}: <strong>{legInfo.performancePercentage}%</strong>
              </span>
            )}
          </small>
        </div>
      </div>

      {isFetching && !isLoading && (
        <div className="text-center mb-2">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {/* Leg Root PV Summary Cards */}
      <div className="row mb-3">
        <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("directSales")}</small>
              <h5 className="mb-0" style={{ color: "#1cc88a", fontWeight: "bold" }}>
                {formatNum(legInfo.directSales)}
              </h5>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("groupSales")}</small>
              <h5 className="mb-0" style={{ color: "#4e73df", fontWeight: "bold" }}>
                {formatNum(legInfo.groupSales)}
              </h5>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("personalPurchases")}</small>
              <h5 className="mb-0" style={{ color: "#f6c23e", fontWeight: "bold" }}>
                {formatNum(legInfo.personalPurchases)}
              </h5>
            </div>
          </div>
        </div>
      </div>

      {/* Network Summary Cards */}
      <div className="row mb-3">
        <div className="col-lg-2 col-md-4 col-sm-6 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("activeSellers")}</small>
              <h5 className="mb-0" style={{ fontWeight: "bold" }}>{summary.activeSellers}</h5>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("activeBuyers")}</small>
              <h5 className="mb-0" style={{ fontWeight: "bold" }}>{summary.activeBuyers}</h5>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("totalMembers")}</small>
              <h5 className="mb-0" style={{ fontWeight: "bold" }}>{summary.totalMembers}</h5>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("activeSubscriptions")}</small>
              <h5 className="mb-0" style={{ color: "#1cc88a", fontWeight: "bold" }}>
                {summary.activeSubscriptions}
              </h5>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("expiredSubscriptions")}</small>
              <h5 className="mb-0" style={{ color: "#e74a3b", fontWeight: "bold" }}>
                {summary.expiredSubscriptions}
              </h5>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6 mb-2">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("recruitsCount")}</small>
              <h5 className="mb-0" style={{ fontWeight: "bold" }}>{summary.totalRecruits}</h5>
            </div>
          </div>
        </div>
      </div>

      {/* Top Sellers */}
      {topSellers && topSellers.length > 0 && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-body">
            <h6 className="card-title" style={{ fontSize: "14px" }}>{t("topSellers")}</h6>
            <div className="table-responsive">
              <table className="table table-sm table-hover" style={{ fontSize: "12px" }}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t("username")}</th>
                    <th>{t("performerFullName")}</th>
                    <th>{t("rank")}</th>
                    <th>{t("sponsor")}</th>
                    <th className="text-end">{t("directSales")}</th>
                    <th className="text-end">{t("groupSales")}</th>
                    <th className="text-end">{t("personalPurchases")}</th>
                  </tr>
                </thead>
                <tbody>
                  {topSellers.map((seller, idx) => (
                    <tr key={seller.userId}>
                      <td>
                        {idx < 3 ? (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: idx === 0 ? "#f6c23e" : idx === 1 ? "#c0c0c0" : "#cd7f32",
                              color: "#fff",
                              fontSize: "10px",
                            }}
                          >
                            {idx + 1}
                          </span>
                        ) : (
                          <span className="text-muted">{idx + 1}</span>
                        )}
                      </td>
                      <td><strong>{seller.username}</strong></td>
                      <td className="text-muted">{seller.fullName || "-"}</td>
                      <td><RankBadge rankName={seller.rankName} /></td>
                      <td>
                        <SponsorCell username={seller.sponsorUsername} fullName={seller.sponsorFullName} />
                      </td>
                      <td className="text-end">
                        <strong style={{ color: "#1cc88a" }}>{formatNum(seller.directSales)}</strong>
                      </td>
                      <td className="text-end">
                        <strong style={{ color: "#4e73df" }}>{formatNum(seller.groupSales)}</strong>
                      </td>
                      <td className="text-end">
                        <strong style={{ color: "#f6c23e" }}>{formatNum(seller.personalPurchases)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="card-title mb-0" style={{ fontSize: "14px" }}>{t("membersList")}</h6>
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">{t("sortBy")}:</small>
              <select
                className="form-select form-select-sm"
                style={{ fontSize: "12px", width: "auto" }}
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
            </div>
          </div>
          {members && members.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table table-sm table-hover" style={{ fontSize: "12px" }}>
                  <thead>
                    <tr>
                      <th>{t("username")}</th>
                      <th>{t("performerFullName")}</th>
                      <th>{t("rank")}</th>
                      <th>{t("sponsor")}</th>
                      <th className="text-center">{t("depth")}</th>
                      <th>{t("dateOfJoining")}</th>
                      <th className="text-center">{t("subscriptionStatus")}</th>
                      <th className="text-end">{t("directSales")}</th>
                      <th className="text-end">{t("groupSales")}</th>
                      <th className="text-end">{t("personalPurchases")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member.userId}>
                        <td><strong>{member.username}</strong></td>
                        <td className="text-muted">{member.fullName || "-"}</td>
                        <td><RankBadge rankName={member.rankName} /></td>
                        <td>
                          <SponsorCell username={member.sponsorUsername} fullName={member.sponsorFullName} />
                        </td>
                        <td className="text-center">
                          <span className="badge bg-light text-dark" style={{ fontSize: "10px" }}>
                            {member.depth}
                          </span>
                        </td>
                        <td style={{ fontSize: "11px" }}>{formatDate(member.dateOfJoining)}</td>
                        <td className="text-center">
                          <SubscriptionBadge active={member.subscriptionActive} productValidity={member.productValidity} t={t} />
                        </td>
                        <td className="text-end">
                          <strong style={{ color: "#1cc88a" }}>{formatNum(member.directSales)}</strong>
                        </td>
                        <td className="text-end">
                          <strong style={{ color: "#4e73df" }}>{formatNum(member.groupSales)}</strong>
                        </td>
                        <td className="text-end">
                          <strong style={{ color: "#f6c23e" }}>{formatNum(member.personalPurchases)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">
                    {t("page")} {page} {t("of")} {totalPages}
                  </small>
                  <div>
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                    >
                      {t("previous")}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page >= totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      {t("next")}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted text-center py-3">{t("noDataForPeriod")}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const LegPerformanceSection = ({ legs, isLoading, isFetching, startDate, endDate }) => {
  const { t } = useTranslation();
  const [drilldownLegId, setDrilldownLegId] = useState(null);

  if (drilldownLegId) {
    return (
      <LegDrilldownView
        legId={drilldownLegId}
        startDate={startDate}
        endDate={endDate}
        onBack={() => setDrilldownLegId(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" /> {t("loading")}
      </div>
    );
  }

  if (!legs || legs.length === 0) {
    return <div className="alert alert-info">{t("noDataAvailable")}</div>;
  }

  const formatNum = (n) => {
    if (n == null) return "-";
    if (isNaN(n)) return "0";
    return Number(n).toLocaleString();
  };

  return (
    <div>
      {isFetching && !isLoading && (
        <div className="text-center mb-2">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {/* Leg Comparison Chart */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body">
          <h6 className="card-title" style={{ fontSize: "14px" }}>{t("legComparison")}</h6>
          <LegComparisonChart legs={legs} />
        </div>
      </div>

      {/* Leg Cards */}
      <div className="row">
        {legs.map((leg) => (
          <div key={leg.legId} className="col-lg-4 col-md-6 mb-3">
            <div
              className="card shadow-sm border-0 h-100"
              style={{ cursor: "pointer" }}
              onClick={() => setDrilldownLegId(leg.legId)}
            >
              <div className="card-header bg-light py-2 d-flex justify-content-between align-items-center">
                <div>
                  <strong style={{ fontSize: "13px" }}>
                    {t("legLabel")} {leg.legPosition}: {leg.username}
                  </strong>
                  <div style={{ marginTop: "2px" }}>
                    <SubscriptionBadge active={leg.subscriptionActive} productValidity={leg.productValidity} t={t} />
                    {leg.rankName && (
                      <span className="ms-1"><RankBadge rankName={leg.rankName} /></span>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  {leg.fullName && (
                    <small className="text-muted me-2">{leg.fullName}</small>
                  )}
                  <span style={{ fontSize: "14px", color: "#4e73df" }} title={t("viewDetails")}>→</span>
                </div>
              </div>
              <div className="card-body" style={{ fontSize: "12px" }}>
                <div className="row g-2">
                  <div className="col-6">
                    <div className="text-muted">{t("directSales")}</div>
                    <strong style={{ color: "#1cc88a" }}>{formatNum(leg.directSales)}</strong>
                  </div>
                  <div className="col-6">
                    <div className="text-muted">{t("groupSales")}</div>
                    <strong style={{ color: "#4e73df" }}>{formatNum(leg.groupSales)}</strong>
                  </div>
                  <div className="col-6">
                    <div className="text-muted">{t("personalPurchases")}</div>
                    <strong style={{ color: "#f6c23e" }}>{formatNum(leg.personalPurchases)}</strong>
                  </div>
                  <div className="col-6">
                    <div className="text-muted">{t("activeSellers")}</div>
                    <strong>{leg.activeSellers}</strong>
                  </div>
                  <div className="col-6">
                    <div className="text-muted">{t("activeBuyers")}</div>
                    <strong>{leg.activeBuyers}</strong>
                  </div>
                  <div className="col-6">
                    <div className="text-muted">{t("totalMembers")}</div>
                    <strong>{formatNum(leg.totalMembers)}</strong>
                  </div>
                  <div className="col-6">
                    <div className="text-muted">{t("recruitsCount")}</div>
                    <strong>{leg.totalRecruits}</strong>
                  </div>
                  <div className="col-6">
                    <div className="text-muted">{t("performancePercentage")}</div>
                    <strong style={{ color: leg.performancePercentage > 0 ? "#1cc88a" : "#858796" }}>
                      {leg.performancePercentage}%
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LegPerformanceSection;
